import React, { memo, useState } from 'react';
import { Typography } from '@mui/material';
import { DataHeading } from '../Charts.styled';
import { SelectMenuItem } from '../Header/Header.styled';
import { SelectWrapper } from '../../../components/common/InputDropdown';
import { ReactComponent as CheckIcon } from '../../../assets/icons/base/check.svg';
import { Box } from '@mui/system';
import { availableAlertsMode, availableIntervals, availableReadsMode } from '../Charts.constants';
import {
  AggregatedAlertsData,
  AggregatedReadsData,
  AvailableFilters, IntervalType,
  PickedFilters,
} from '../Charts.types';
import { TableHolder } from './TableTab.styled';
import { DataGrid } from '@mui/x-data-grid';
import NoData from '../NoData';
import { CircleProgress } from '../../../components/common/CircleProgress';

type LineGraphType = {
  readsData: AggregatedReadsData;
  alertsData: AggregatedAlertsData;
  type: 'all' | 'reads' | 'alerts';
  intervalForReads: IntervalType;
  intervalForAlerts: IntervalType;
  setIntervalForReads: React.Dispatch<React.SetStateAction<IntervalType>>;
  setIntervalForAlerts: React.Dispatch<React.SetStateAction<IntervalType>>;
  availableFilters: AvailableFilters;
  pickedFilters: PickedFilters;
  setPickedFilters: React.Dispatch<React.SetStateAction<PickedFilters>>;
  loading: boolean;
  readsMode: 'site' | 'camera';
  alertsMode: 'site' | 'camera' | 'alert';
  setReadsMode: React.Dispatch<React.SetStateAction<'site' | 'camera'>>;
  setAlertsMode: React.Dispatch<React.SetStateAction<'site' | 'camera' | 'alert'>>;
};
const TableTab = (props: LineGraphType) => {
  const {
    type, readsData, intervalForReads,
    setIntervalForReads, alertsData, intervalForAlerts,
    setIntervalForAlerts, availableFilters, pickedFilters, setPickedFilters,
    loading, readsMode, setReadsMode, alertsMode, setAlertsMode,
  } = props;

  // Different data source for different mode
  const readsColumns = Object.keys(
    (readsMode === 'site' ? readsData.sitesData[0] : readsData.camerasData[0]) || {},
  ).reverse().filter((key) => key !== 'correctDate').map((key) => {
    return ({
      field: key,
      headerName: key,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography
              color={'grey.500'}
              variant={'tsm'}
              fontWeight={'400'}
            >
              {params.row[key]}
            </Typography>
          </Box>
        );
      },
    });
  });

  // Different data source for different mode
  const dateColumn = {
    field: 'correctDate',
    headerName: 'Date/Time',
    flex: 1,
    renderCell: (params) => {
      return (
        <Box>
          <Typography
            color={'grey.900'}
            variant={'tsm'}
            fontWeight={'500'}
          >
            {params.row.correctDate}
          </Typography>
        </Box>
      );
    },
  };

  const alertsColumns = Object.keys(
    (alertsMode === 'alert' && alertsData.alertsData[0]) || (alertsMode === 'site' ? alertsData.sitesData[0] : alertsData.camerasData[0]) || {},
  ).reverse().filter((key) => key !== 'correctDate').map((key) => {
    return ({
      field: key,
      headerName: key,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography
              color={'grey.500'}
              variant={'tsm'}
              fontWeight={'400'}
            >
              {params.row[key]}
            </Typography>
          </Box>
        );
      },
    });
  });

  alertsColumns.unshift(dateColumn);
  readsColumns.unshift(dateColumn);
  const [alertPageSize, setAlertPageSize] = useState<number>(25);
  const [readsPageSize, setReadsPageSize] = useState<number>(25);
  return (
    <>
      {(type === 'all' || type === 'reads') && (
        <Box>
          <DataHeading>
            <Typography variant="txl" fontWeight="600">
              Reads over time
            </Typography>
            <Box display="flex" gap="16px">
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setReadsMode(e.target.value)}
                  value={readsMode}
                  renderValue={(selected) => {
                    return <span>{selected === 'site' ? 'By Site' : 'By Camera'}</span>;
                  }}
                  displayEmpty
                >
                  {availableReadsMode.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === readsMode && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setIntervalForReads(e.target.value)}
                  value={intervalForReads}
                  renderValue={(selected) => {
                    return <span>{availableIntervals.find((d) => d.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableIntervals.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === intervalForReads && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
            </Box>
          </DataHeading>
          <TableHolder>
            {!loading && (
              <DataGrid
                getRowId={(row) => row.correctDate}
                sx={{
                  border: 'none',
                }}
                components={{
                  NoRowsOverlay: NoData,
                }}
                scrollbarSize={0}
                headerHeight={44}
                rowHeight={72}
                rows={readsMode === 'site' ? readsData.sitesData : readsData.camerasData}
                columns={readsColumns}
                pageSize={readsPageSize}
                onPageSizeChange={(size) => setReadsPageSize(size)}
                disableSelectionOnClick
              />
            )}
            {loading && (
              <Box
                height={'640px !important'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <CircleProgress />
              </Box>
            )}
          </TableHolder>
        </Box>
      )}
      {(type === 'all' || type === 'alerts') && (
        <Box>
          <DataHeading>
            <Typography variant="txl" fontWeight="600">
              Alerts over time
            </Typography>
            <Box display="flex" gap="16px">
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setAlertsMode(e.target.value)}
                  value={alertsMode}
                  renderValue={(selected) => {
                    return <span>{availableAlertsMode.find((aam) => aam.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableAlertsMode.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === alertsMode && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setPickedFilters({ ...pickedFilters, alerts: e.target.value })}
                  value={pickedFilters.alerts}
                  renderValue={(selected: any) => {
                    return <span>{selected.length} Alarm Classes</span>;
                  }}
                  multiple
                  displayEmpty
                >
                  {availableFilters.alerts.map((arm) => (
                    <SelectMenuItem key={arm.id} value={arm.id}>
                      {arm.name}
                      {pickedFilters.alerts.includes(arm.id) && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setIntervalForAlerts(e.target.value)}
                  value={intervalForAlerts}
                  renderValue={(selected) => {
                    return <span>{availableIntervals.find((d) => d.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableIntervals.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === intervalForAlerts && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
            </Box>
          </DataHeading>
          <TableHolder>
            {!loading && (
              <DataGrid
                getRowId={(row) => row.correctDate}
                sx={{
                  border: 'none',
                }}
                components={{
                  NoRowsOverlay: NoData,
                }}
                scrollbarSize={0}
                headerHeight={44}
                rowHeight={72}
                rows={(alertsMode === 'alert' && alertsData.alertsData) || (alertsMode === 'site' ? alertsData.sitesData : alertsData.camerasData)}
                columns={alertsColumns}
                pageSize={alertPageSize}
                onPageSizeChange={(size) => setAlertPageSize(size)}
                disableSelectionOnClick
              />
            )}
            {loading && (
              <Box
                height={'640px !important'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <CircleProgress />
              </Box>
            )}
          </TableHolder>
        </Box>
      )}
    </>
  );
};

export default memo(TableTab);
